/**
 * Who is typing, worked out from when their stamps reached this phone.
 *
 * A stamp is the server's time, and phones' clocks disagree, so the stamp's
 * value is only used once: to throw away the ones already old on arrival.
 * Every listener is handed the whole collection when it subscribes, which
 * includes stamps left by somebody who closed the app mid-sentence. After
 * that, what counts is when a change was heard here, by this phone's clock.
 */
import { TYPING_SHOW_MS, canSee } from '../../shared/constants';
import type { TypingDoc } from '../../shared/types';

/** When each person's stamp last changed, by this phone's clock. */
export type Heard = Record<string, number>;

export interface TypingChange {
  uid: string;
  type: 'added' | 'modified' | 'removed';
  data?: TypingDoc;
  /** The stamp, or null while it is still a server timestamp to come. */
  atMs: number | null;
}

/** Older than this on arrival is somebody who left, not somebody typing. */
export const STALE_MS = 30_000;

/** Folds one snapshot's changes into what has been heard. */
export function hear(
  heard: Heard,
  changes: TypingChange[],
  now: number,
  viewerEmail: string | null | undefined,
): Heard {
  const next = { ...heard };
  for (const change of changes) {
    if (change.type === 'removed') {
      delete next[change.uid];
      continue;
    }
    // A test account's typing is as hidden as what it sends.
    if (change.data && !canSee(change.data, viewerEmail)) {
      delete next[change.uid];
      continue;
    }
    if (change.atMs === null) continue;
    if (Math.abs(now - change.atMs) > STALE_MS) {
      delete next[change.uid];
      continue;
    }
    next[change.uid] = now;
  }
  return next;
}

/** Those still typing, which is never you. */
export function whoIsTyping(heard: Heard, now: number, me: string): string[] {
  return Object.entries(heard)
    .filter(([uid, at]) => uid !== me && now - at < TYPING_SHOW_MS)
    .map(([uid]) => uid);
}

/** Drops whoever has stopped, so an empty map can stop the clock. */
export function forget(heard: Heard, now: number): Heard {
  return Object.fromEntries(Object.entries(heard).filter(([, at]) => now - at < TYPING_SHOW_MS));
}

/**
 * In words, without the ellipsis: the dots beside it are drawn.
 *
 * Alphabetical, so a second person starting does not shuffle the first one's
 * name to a new place in the line.
 */
export function typingLine(names: string[]): string | null {
  const sorted = [...names].sort((a, b) => a.localeCompare(b));
  if (sorted.length === 0) return null;
  if (sorted.length === 1) return `${sorted[0]} is typing`;
  if (sorted.length === 2) return `${sorted[0]} and ${sorted[1]} are typing`;
  if (sorted.length === 3) return `${sorted[0]}, ${sorted[1]} and ${sorted[2]} are typing`;
  return 'Several people are typing';
}
